import { jsPDF } from 'jspdf';
import { formatPKR } from './currencyFormatter';
import { formatDate } from './dateFormatter';
import { addPdfBrandLogo, PDF_BRAND_LOGO_URL } from './pdfLogo';

export const SALARY_SLIP_LOGO_URL = PDF_BRAND_LOGO_URL;

const FOOTER_HEIGHT_MM = 10;
const SIGNATURE_BLOCK_HEIGHT_MM = 20;
const BOTTOM_RESERVE_MM = FOOTER_HEIGHT_MM + SIGNATURE_BLOCK_HEIGHT_MM;
const SHORT_MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function statusLabel(status) {
  if (status === 'settled') return 'Already settled';
  if (status === 'partial') return 'Partially paid';
  return 'Unpaid';
}

function partyBalanceText(balance) {
  const n = parseFloat(balance) || 0;
  if (n > 0) return `${formatPKR(n)} (To receive)`;
  if (n < 0) return `${formatPKR(Math.abs(n))} (We owe)`;
  return formatPKR(0);
}

const toAmount = (n) => {
  const value = parseFloat(n);
  return Number.isFinite(value) ? value : 0;
};

const safeText = (value) => {
  if (value == null) return '—';
  const text = String(value).trim();
  return text || '—';
};

const entryTypeLabel = (entry) => {
  if (entry.entry_type_label) return entry.entry_type_label;
  const ref = entry.reference_type || entry.referenceType || '';
  if (ref === 'salary') return 'Salary payment';
  if (ref === 'salary_advance') return 'Advance';
  return ref.replace(/_/g, ' ') || 'Entry';
};

function createEnsureSpace(doc, pageH, margin) {
  return (currentY, neededHeight) => {
    if (currentY + neededHeight > pageH - BOTTOM_RESERVE_MM) {
      doc.addPage();
      return margin;
    }
    return currentY;
  };
}

function drawDetailRow(doc, state, label, value) {
  const { labelX, valueX, valueMaxW, pageH, margin } = state;
  const lines = doc.splitTextToSize(String(value), valueMaxW);
  const rowHeight = Math.max(6.5, lines.length * 5.2);
  state.y = state.ensureSpace(state.y, rowHeight);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(55, 65, 81);
  doc.text(`${label}:`, labelX, state.y);
  doc.setFont('helvetica', 'normal');
  doc.text(lines, valueX, state.y);
  state.y += rowHeight;
}

function drawPdfHeader(doc, state, { businessName, branchName, title, subtitle }) {
  const { pageW } = state;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(30, 41, 59);
  doc.text(String(businessName || 'Business').trim() || 'Business', pageW / 2, state.y, { align: 'center' });
  state.y += 8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  if (branchName) {
    doc.text(`Branch: ${branchName}`, pageW / 2, state.y, { align: 'center' });
    state.y += 6;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.text(title, pageW / 2, state.y, { align: 'center' });
  state.y += 7;

  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(71, 85, 105);
    doc.text(subtitle, pageW / 2, state.y, { align: 'center' });
    state.y += 10;
  }
}

function drawSignatures(doc, state) {
  const { pageW, pageH, margin } = state;
  const signatureLineWidth = 56;
  const signatureGap = (pageW - margin * 2 - signatureLineWidth * 3) / 2;
  const signatureY = state.y + 14;

  if (signatureY + SIGNATURE_BLOCK_HEIGHT_MM > pageH - FOOTER_HEIGHT_MM) {
    doc.addPage();
    state.y = margin;
  } else {
    state.y = signatureY;
  }

  const employeeSigX = margin;
  const accountsSigX = margin + signatureLineWidth + signatureGap;
  const employerSigX = pageW - margin - signatureLineWidth;

  doc.setDrawColor(148, 163, 184);
  doc.line(employeeSigX, state.y, employeeSigX + signatureLineWidth, state.y);
  doc.line(accountsSigX, state.y, accountsSigX + signatureLineWidth, state.y);
  doc.line(employerSigX, state.y, employerSigX + signatureLineWidth, state.y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text('Employee signature', employeeSigX, state.y + 5);
  doc.text('Accounts signature', accountsSigX, state.y + 5);
  doc.text('Authorized signature', employerSigX, state.y + 5);
}

function drawPageFooters(doc, { margin, pageW, pageH, footText }) {
  const totalPages = doc.getNumberOfPages();
  const footLines = doc.splitTextToSize(footText, pageW - margin * 2);
  const footLineH = 3.8;
  const footBlockH = footLines.length * footLineH;

  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    const stampY = pageH - 6;
    doc.text(`Page ${page} of ${totalPages}`, pageW - margin, stampY, { align: 'right' });
    if (page === totalPages) {
      const footY = pageH - FOOTER_HEIGHT_MM - footBlockH + footLineH;
      doc.text(footLines, margin, Math.max(margin, footY));
    }
  }
}

async function initPdfState() {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const { nextY } = await addPdfBrandLogo(doc, { maxWidthMm: 38, startY: 10, gapAfterMm: 6 });
  const labelX = margin;
  const valueX = margin + 54;
  const valueMaxW = pageW - valueX - margin;
  const ensureSpace = createEnsureSpace(doc, pageH, margin);

  return {
    doc,
    pageW,
    pageH,
    margin,
    labelX,
    valueX,
    valueMaxW,
    y: nextY,
    ensureSpace,
  };
}

function drawTransactionTable(doc, state, ledgerEntries, emptyMessage) {
  const { pageW, pageH, margin } = state;
  const transactions = Array.isArray(ledgerEntries)
    ? ledgerEntries.filter((entry) => {
      const ref = entry.reference_type || entry.referenceType || '';
      return ref === 'salary' || ref === 'salary_advance';
    })
    : [];

  state.y = state.ensureSpace(state.y, 16);
  state.y += 4;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(17, 24, 39);
  doc.text('Payment transactions', margin, state.y);
  state.y += 6;

  const txCols = {
    date: margin,
    type: margin + 28,
    description: margin + 62,
    amount: pageW - margin,
  };
  const descMaxW = txCols.amount - txCols.description - 8;

  const drawTransactionHeader = () => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text('Date', txCols.date, state.y);
    doc.text('Type', txCols.type, state.y);
    doc.text('Description', txCols.description, state.y);
    doc.text('Amount', txCols.amount, state.y, { align: 'right' });
    doc.setDrawColor(203, 213, 225);
    doc.line(margin, state.y + 1.5, pageW - margin, state.y + 1.5);
    state.y += 6;
  };

  drawTransactionHeader();
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(30, 41, 59);

  if (transactions.length === 0) {
    state.y = state.ensureSpace(state.y, 6);
    doc.text(emptyMessage, margin, state.y);
    state.y += 6;
    return;
  }

  transactions.forEach((entry) => {
    const dateText = formatDate(entry.transaction_date || entry.transactionDate);
    const typeText = entryTypeLabel(entry);
    const meta = entry.meta || {};
    const method = meta.paymentMethod || meta.payment_method;
    const descParts = [entry.description || '—'];
    if (method) descParts.push(`Method: ${method}`);
    const descText = descParts.filter(Boolean).join(' · ');
    const descLines = doc.splitTextToSize(descText, descMaxW);
    const rowHeight = Math.max(5.5, descLines.length * 4.8);

    if (state.y + rowHeight > pageH - BOTTOM_RESERVE_MM) {
      doc.addPage();
      state.y = margin;
      drawTransactionHeader();
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(30, 41, 59);
    }

    const amount = toAmount(entry.debit);
    doc.text(dateText, txCols.date, state.y);
    doc.text(typeText, txCols.type, state.y);
    doc.text(descLines, txCols.description, state.y);
    doc.text(formatPKR(amount), txCols.amount, state.y, { align: 'right' });
    state.y += rowHeight;
  });
}

/**
 * Monthly salary slip for the selected pay period.
 */
export async function downloadMonthlySalarySlipPdf(options) {
  const {
    businessName,
    branchName = '',
    employee,
    periodLabel,
    periodMonth,
    periodYear,
    generatedBy = '',
    ledgerEntries = [],
    generatedAt = new Date()
  } = options;

  if (!employee) throw new Error('Employee is required');

  const state = await initPdfState();
  const { doc } = state;

  const monthlySalary = toAmount(employee.monthly_salary);
  const monthPaid = toAmount(employee.paid_for_month || 0);
  const monthAdvance = toAmount(employee.advances_allocated_to_month || 0);
  const monthOutstanding = toAmount(employee.outstanding_for_month ?? 0);
  const monthAllocated = monthPaid + monthAdvance;
  const netPayable = Math.max(0, monthlySalary - monthAdvance);

  drawPdfHeader(doc, state, {
    businessName,
    branchName,
    title: 'Salary Slip',
    subtitle: `Pay period: ${periodLabel || '—'}`,
  });

  state.y = state.ensureSpace(state.y, 14);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(17, 24, 39);
  doc.text('Employee details', state.margin, state.y);
  state.y += 6;

  drawDetailRow(doc, state, 'Employee', employee.name || '—');
  drawDetailRow(doc, state, 'Phone', employee.phone || '—');
  drawDetailRow(doc, state, 'Pay period', periodLabel || '—');

  state.y = state.ensureSpace(state.y, 14);
  state.y += 2;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(17, 24, 39);
  doc.text('Salary summary', state.margin, state.y);
  state.y += 6;

  const summaryRows = [
    ['Gross salary', formatPKR(monthlySalary)],
    ['Advances allocated', formatPKR(monthAdvance)],
    ['Net payable', formatPKR(netPayable)],
    ['Salary paid', formatPKR(monthPaid)],
    ['Total paid / allocated', formatPKR(monthAllocated)],
    ['Outstanding', formatPKR(monthOutstanding)],
    ['Status', statusLabel(employee.status)],
    ['Generated on', generatedAt.toLocaleString('en-PK')],
    ['Generated by', safeText(generatedBy)],
    ['Print date', formatDate(new Date())],
  ];
  summaryRows.forEach(([label, value]) => drawDetailRow(doc, state, label, value));

  drawTransactionTable(
    doc,
    state,
    ledgerEntries,
    'No salary or advance entries recorded for this month.'
  );

  drawSignatures(doc, state);
  drawPageFooters(doc, {
    margin: state.margin,
    pageW: state.pageW,
    pageH: state.pageH,
    footText: 'This salary slip is generated from salary and advance entries allocated to the selected month. Please verify with payroll records before final sign-off.',
  });

  const safeName = String(employee.name || 'employee')
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 48);
  const safePeriod = String(periodLabel || `${periodYear}-${periodMonth}`).replace(/[^\w-]/g, '-');
  doc.save(`salary-slip-${safeName}-${safePeriod}.pdf`);
}

/**
 * Annual salary statement for the selected year.
 */
export async function downloadAnnualSalarySlipPdf(options) {
  const {
    businessName,
    branchName = '',
    employee,
    periodLabel,
    year,
    generatedBy = '',
    ledgerEntries = [],
    monthlySummary = [],
    generatedAt = new Date()
  } = options;

  if (!employee) throw new Error('Employee is required');

  const state = await initPdfState();
  const { doc } = state;
  const selectedYear = parseInt(year, 10) || new Date().getFullYear();

  const monthlySalary = toAmount(employee.monthly_salary);
  const thisMonthPaid = toAmount(employee.paid_for_month || 0);
  const thisMonthAdvance = toAmount(employee.advances_allocated_to_month || 0);
  const thisMonthOutstanding = toAmount(employee.outstanding_for_month ?? 0);

  const yearlySalaryRows = Array.isArray(ledgerEntries)
    ? ledgerEntries.filter((entry) => (entry.reference_type || entry.referenceType) === 'salary')
    : [];
  const yearlyAdvanceRows = Array.isArray(ledgerEntries)
    ? ledgerEntries.filter((entry) => (entry.reference_type || entry.referenceType) === 'salary_advance')
    : [];
  const yearlyPaid = yearlySalaryRows.reduce((sum, row) => sum + toAmount(row.debit), 0);
  const yearlyAdvance = yearlyAdvanceRows.reduce((sum, row) => sum + toAmount(row.debit), 0);
  const yearlyGross = monthlySalary * 12;
  const yearlyNet = Math.max(0, yearlyGross - yearlyAdvance);
  const yearOutstanding = Math.max(0, yearlyNet - yearlyPaid);

  drawPdfHeader(doc, state, {
    businessName,
    branchName,
    title: `Annual Salary Statement (${selectedYear})`,
    subtitle: periodLabel ? `Current view: ${periodLabel}` : null,
  });

  state.y = state.ensureSpace(state.y, 14);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(17, 24, 39);
  doc.text('Employee details', state.margin, state.y);
  state.y += 6;

  const detailRows = [
    ['Employee', employee.name || '—'],
    ['Phone', employee.phone || '—'],
    ['Statement year', selectedYear],
    ['Monthly salary', formatPKR(monthlySalary)],
    ['Expected gross (year)', formatPKR(yearlyGross)],
    ['Advance allocated (year)', formatPKR(yearlyAdvance)],
    ['Paid amount (year)', formatPKR(yearlyPaid)],
    ['Expected net after advances', formatPKR(yearlyNet)],
    ['Outstanding for year', formatPKR(yearOutstanding)],
    ['Current month paid', formatPKR(thisMonthPaid)],
    ['Current month advance', formatPKR(thisMonthAdvance)],
    ['Current month outstanding', formatPKR(thisMonthOutstanding)],
    ['Party ledger balance', partyBalanceText(employee.balance)],
    ['Status', statusLabel(employee.status)],
    ['Generated on', generatedAt.toLocaleString('en-PK')],
    ['Generated by', safeText(generatedBy)],
    ['Print date', formatDate(new Date())],
  ];
  detailRows.forEach(([label, value]) => drawDetailRow(doc, state, label, value));

  const monthMap = {};
  (Array.isArray(monthlySummary) ? monthlySummary : []).forEach((row) => {
    const m = parseInt(row.month, 10);
    if (!m || m < 1 || m > 12) return;
    monthMap[m] = row;
  });

  state.y = state.ensureSpace(state.y, 20);
  state.y += 2;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(17, 24, 39);
  doc.text('Monthly breakdown', state.margin, state.y);
  state.y += 6;

  const { pageW, pageH, margin } = state;
  const monthColX = margin;
  const salaryColX = margin + 52;
  const advanceColX = margin + 86;
  const paidColX = margin + 118;
  const outstandingColX = margin + 152;
  const breakdownRowHeight = 5.5;

  const drawBreakdownHeader = () => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text('Month', monthColX, state.y);
    doc.text('Salary', salaryColX, state.y, { align: 'right' });
    doc.text('Advance', advanceColX, state.y, { align: 'right' });
    doc.text('Paid', paidColX, state.y, { align: 'right' });
    doc.text('Outstanding', outstandingColX, state.y, { align: 'right' });
    doc.setDrawColor(203, 213, 225);
    doc.line(margin, state.y + 1.5, pageW - margin, state.y + 1.5);
    state.y += 6;
  };

  drawBreakdownHeader();
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(30, 41, 59);

  for (let monthIdx = 1; monthIdx <= 12; monthIdx += 1) {
    if (state.y + breakdownRowHeight > pageH - BOTTOM_RESERVE_MM) {
      doc.addPage();
      state.y = margin;
      drawBreakdownHeader();
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(30, 41, 59);
    }

    const row = monthMap[monthIdx] || {};
    const accrued = row.salary_accrued != null ? toAmount(row.salary_accrued) : monthlySalary;
    const advance = toAmount(row.advances_allocated || 0);
    const paid = toAmount(row.salary_paid || 0);
    const outstanding = Math.max(
      0,
      row.outstanding != null ? toAmount(row.outstanding) : (accrued - paid - advance)
    );

    doc.text(`${SHORT_MONTH[monthIdx - 1]} ${selectedYear}`, monthColX, state.y);
    doc.text(formatPKR(accrued), salaryColX, state.y, { align: 'right' });
    doc.text(formatPKR(advance), advanceColX, state.y, { align: 'right' });
    doc.text(formatPKR(paid), paidColX, state.y, { align: 'right' });
    doc.text(formatPKR(outstanding), outstandingColX, state.y, { align: 'right' });
    state.y += breakdownRowHeight;
  }

  drawSignatures(doc, state);
  drawPageFooters(doc, {
    margin: state.margin,
    pageW: state.pageW,
    pageH: state.pageH,
    footText: 'This annual salary statement is generated from salary and advance entries in the employee ledger. Please verify with payroll records before final sign-off.',
  });

  const safeName = String(employee.name || 'employee')
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 48);
  doc.save(`annual-salary-slip-${safeName}-${selectedYear}.pdf`);
}

/** @deprecated Use downloadMonthlySalarySlipPdf */
export const downloadSalarySlipPdf = downloadMonthlySalarySlipPdf;
